import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Clock, Linkedin, ShoppingCart, CreditCard, BarChart3, Code2, Users, Store, ShieldCheck, Lock, Zap, ShoppingBag, TrendingUp, DollarSign, Check, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { getApiUrl } from '@/lib/api';

// Dark theme logo - clean bricks
const Logo = () => (
  <a href="/" className="flex items-center gap-2.5 group">
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="transition-transform duration-300 group-hover:scale-105">
      <rect x="0" y="1" width="14" height="6" rx="1" fill="#f43f5e" />
      <rect x="16" y="1" width="16" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="9" width="9" height="6" rx="1" fill="#e11d48" />
      <rect x="11" y="9" width="11" height="6" rx="1" fill="#f43f5e" />
      <rect x="24" y="9" width="8" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="17" width="15" height="6" rx="1" fill="#f43f5e" />
      <rect x="17" y="17" width="15" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="25" width="10" height="6" rx="1" fill="#e11d48" />
      <rect x="12" y="25" width="12" height="6" rx="1" fill="#f43f5e" />
      <rect x="26" y="25" width="6" height="6" rx="1" fill="#e11d48" />
    </svg>
    <span className="text-lg font-semibold tracking-tight text-white">
      saleswall
    </span>
  </a>
);

// Light version for dark backgrounds
const LogoLight = () => (
  <a href="/" className="flex items-center gap-2.5 group">
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="transition-transform duration-300 group-hover:scale-105">
      <rect x="0" y="1" width="14" height="6" rx="1" fill="#f43f5e" />
      <rect x="16" y="1" width="16" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="9" width="9" height="6" rx="1" fill="#e11d48" />
      <rect x="11" y="9" width="11" height="6" rx="1" fill="#f43f5e" />
      <rect x="24" y="9" width="8" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="17" width="15" height="6" rx="1" fill="#f43f5e" />
      <rect x="17" y="17" width="15" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="25" width="10" height="6" rx="1" fill="#e11d48" />
      <rect x="12" y="25" width="12" height="6" rx="1" fill="#f43f5e" />
      <rect x="26" y="25" width="6" height="6" rx="1" fill="#e11d48" />
    </svg>
    <span className="text-lg font-semibold tracking-tight text-white">
      saleswall
    </span>
  </a>
);

// Header
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0b]/90 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <LogoLight />
          <div className="flex items-center gap-6">
            <a href="#waitlist">
              <Button className="bg-rose-500 hover:bg-rose-600 text-white text-sm px-5 py-2 h-auto rounded-lg transition-all hover:shadow-lg hover:shadow-rose-500/25">
                Join Waitlist
              </Button>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

// Email Mockup Component
const InboxChaosMockup = () => (
  <div className="relative">
    <div className="absolute -inset-4 bg-rose-500/10 rounded-3xl blur-2xl" />
    <div className="relative bg-[#18181b] rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
      {/* Header */}
      <div className="px-4 py-3 bg-rose-500/10 border-b border-white/5">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm font-semibold text-rose-400">The Problem: Inbox Chaos</span>
        </div>
      </div>

      {/* Browser bar */}
      <div className="flex items-center gap-2 px-4 py-2 bg-[#0f0f10] border-b border-white/5">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
          <div className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
          <div className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
        </div>
        <div className="flex-1 mx-3">
          <div className="bg-[#18181b] rounded px-3 py-1 text-xs text-zinc-500 border border-white/5">Inbox — 47 unread</div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Vendor Email */}
        <div className="bg-[#0f0f10] rounded-xl p-4 border border-white/5">
          <div className="flex items-start gap-3 mb-3">
            <img
              src="https://images.unsplash.com/photo-1680104072720-ae824ef15c0e?w=100&h=100&fit=crop&crop=face"
              alt="Mike"
              className="w-8 h-8 rounded-full object-cover"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-white">Mike from PayStream360</span>
                <span className="text-[10px] px-1.5 py-0.5 bg-rose-500/20 text-rose-400 rounded">Sales</span>
              </div>
              <div className="text-xs text-zinc-500">to: you@company.com</div>
            </div>
          </div>
          <div className="text-sm text-zinc-400 leading-relaxed">
            <p className="font-medium text-white mb-2">Re: Quick question about your payment processing</p>
            <p>Hi! We help e-commerce stores save <span className="text-rose-400 font-semibold">up to 40% on fees</span>. Can I grab 15 mins this week?</p>
          </div>
        </div>

        {/* More emails */}
        <div className="space-y-2 opacity-60">
          {[
            { initials: 'AL', name: 'AdLift Pro', subject: '"10x your ROAS guaranteed"' },
            { initials: 'SF', name: 'ShipFast', subject: '"Same-day delivery, any scale"' },
            { initials: 'RM', name: 'RetailMax CRM', subject: '"Boost LTV by 60%"' },
          ].map((email, i) => (
            <div key={i} className="bg-[#0f0f10] rounded-lg p-3 border border-white/5 flex items-center gap-3">
              <div className="w-6 h-6 bg-rose-500/20 rounded-full flex items-center justify-center">
                <span className="text-[10px] font-bold text-rose-400">{email.initials}</span>
              </div>
              <div className="flex-1">
                <div className="text-xs font-medium text-zinc-300">{email.name} — {email.subject}</div>
              </div>
              <div className="w-2 h-2 bg-rose-500 rounded-full"></div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Floating indicator */}
    <div className="absolute -bottom-4 -left-4 bg-[#18181b] rounded-xl border border-white/10 p-4 shadow-xl z-10">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-rose-500/20 rounded-full flex items-center justify-center">
          <Mail className="w-5 h-5 text-rose-500" />
        </div>
        <div>
          <div className="text-xs font-semibold text-white">47 vendor pitches</div>
          <div className="text-[11px] text-rose-400">No way to verify claims</div>
        </div>
      </div>
    </div>

    {/* Time indicator */}
    <div className="absolute -top-3 -right-4 bg-[#18181b] rounded-xl border border-white/10 p-3 shadow-xl z-10">
      <div className="text-[11px] text-zinc-500 mb-0.5">Time lost this week</div>
      <div className="text-lg font-bold text-rose-500">8.5 hrs</div>
    </div>
  </div>
);

// Hero Section
const HeroSection = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      await axios.post(getApiUrl('/api/waitlist'), { email: email.trim(), source: 'hero' });
      setStatus('success');
      setEmail('');
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <section className="relative min-h-screen flex items-center pt-16 overflow-hidden bg-[#0a0a0b]">
      {/* Background effects */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: 'radial-gradient(circle at 50% 50%, #f43f5e 0%, transparent 50%)',
      }} />
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
        backgroundSize: '32px 32px',
      }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-500/10 border border-rose-500/20 rounded-full mb-8">
              <span className="w-2 h-2 bg-rose-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-rose-400 tracking-wide uppercase">
                Early Access
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6">
              See what matters.
              <br />
              <span className="text-zinc-500">Ignore the noise.</span>
            </h1>

            <p className="text-xl text-zinc-400 max-w-xl mb-10 leading-relaxed">
              Saleswall turns messy inbound sales activity into one clean weekly signal.
            </p>

            {status === 'success' ? (
              <div className="text-emerald-400 text-lg mb-8">
                You're on the list. We'll be in touch.
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-start gap-3 mb-8">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your work email"
                  required
                  disabled={status === 'loading'}
                  className="w-full sm:flex-1 sm:max-w-sm px-5 py-4 text-base border border-white/10 rounded-xl focus:outline-none focus:border-rose-500/50 bg-white/5 text-white transition-all placeholder:text-zinc-500"
                />
                <Button
                  type="submit"
                  disabled={status === 'loading'}
                  className="bg-rose-500 hover:bg-rose-600 text-white px-8 py-4 h-auto text-base font-medium rounded-xl transition-all hover:shadow-xl hover:shadow-rose-500/25 group"
                >
                  {status === 'loading' ? 'Joining...' : 'Join Waitlist'}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </form>
            )}

            {/* Stats */}
            <div className="flex flex-wrap gap-10 pt-8 border-t border-white/10">
              {[
                { value: '94%', label: 'Pitches are noise' },
                { value: '8.5hrs', label: 'Wasted per week' },
                { value: '1', label: 'Signal. All that matters.' },
              ].map((stat, i) => (
                <div key={i}>
                  <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-zinc-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Mockup */}
          <div className="hidden lg:block">
            <InboxChaosMockup />
          </div>
        </div>
      </div>
    </section>
  );
};

// Problem Section with Pain Points
const ProblemSection = () => {
  const painPoints = [
    {
      icon: Mail,
      title: 'Email Overload',
      description: 'Your inbox is drowning in cold emails from sales reps pitching tools you\'ll never need.',
      stat: '127',
      statLabel: 'avg cold emails per week',
    },
    {
      icon: Linkedin,
      title: 'LinkedIn Spam',
      description: 'Connection requests that turn into instant pitches. Your feed is a sales floor.',
      stat: '63',
      statLabel: 'avg pitch messages per week',
    },
    {
      icon: Clock,
      title: 'Time Wasted',
      description: 'Hours spent reading, evaluating, and declining pitches that were never relevant.',
      stat: '8.5hrs',
      statLabel: 'wasted per decision maker weekly',
    },
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#0f0f10] relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-16">
          <span className="text-xs font-medium text-rose-500 tracking-widest uppercase mb-4 block">
            The Problem
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Your team is drowning in
            <span className="text-zinc-600"> unwanted pitches.</span>
          </h2>
          <p className="text-zinc-400 text-lg leading-relaxed">
            Every day, decision makers waste hours sifting through irrelevant sales outreach.
            The few gems get lost in the noise.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {painPoints.map((point, i) => {
            const Icon = point.icon;
            return (
              <div
                key={i}
                className="group relative bg-[#18181b] border border-white/5 rounded-2xl p-8 hover:border-rose-500/30 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-rose-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-rose-500/20 transition-colors">
                  <Icon className="w-5 h-5 text-rose-500" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{point.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6">{point.description}</p>
                <div className="pt-6 border-t border-white/5">
                  <div className="text-2xl font-bold text-rose-500">{point.stat}</div>
                  <div className="text-xs text-zinc-500 mt-1">{point.statLabel}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// How It Works with Mockups
const HowItWorksSection = () => {
  return (
    <section className="py-24 lg:py-32 bg-[#0a0a0b] relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-16">
          <span className="text-xs font-medium text-rose-500 tracking-widest uppercase mb-4 block">
            How It Works
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Three steps to
            <span className="text-zinc-600"> clarity.</span>
          </h2>
        </div>

        <div className="space-y-8">
          {[
            {
              step: '01',
              title: 'Redirect',
              description: 'Give vendors your Saleswall link. They pitch us, not you.',
              mockup: (
                <div className="bg-[#18181b] rounded-xl border border-white/10 p-4 max-w-xs">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-rose-500/20 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-rose-400">JD</span>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-white">Jake from DataSync</div>
                      <div className="text-[11px] text-zinc-500">Sales Development Rep</div>
                    </div>
                  </div>
                  <div className="text-xs text-zinc-400 mb-3">"Would love 15 min to show how DataSync can help..."</div>
                  <div className="flex gap-2">
                    <div className="flex-1 px-3 py-2 bg-white/5 rounded-lg text-[10px] text-zinc-500 text-center">Decline</div>
                    <div className="flex-1 px-3 py-2 bg-rose-500 rounded-lg text-[10px] text-white text-center font-medium">Send to Saleswall</div>
                  </div>
                </div>
              ),
            },
            {
              step: '02',
              title: 'Filter',
              description: 'We evaluate every pitch against your actual needs. No bias. Just data.',
              mockup: (
                <div className="bg-[#18181b] rounded-xl border border-white/10 p-4 max-w-xs">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-white">Evaluation Scorecard</span>
                    <span className="text-[10px] px-2 py-0.5 bg-rose-500/20 text-rose-400 rounded-full">In Review</span>
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { label: 'Relevance', score: 82, color: 'bg-rose-500' },
                      { label: 'Budget Fit', score: 65, color: 'bg-zinc-500' },
                      { label: 'Strategic Align', score: 91, color: 'bg-emerald-500' },
                    ].map((item, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-zinc-500">{item.label}</span>
                          <span className="text-[10px] font-semibold text-zinc-300">{item.score}%</span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.score}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ),
            },
            {
              step: '03',
              title: 'Signal',
              description: 'You get one weekly report. Only what matters.',
              mockup: (
                <div className="bg-[#18181b] rounded-xl border border-white/10 p-4 max-w-xs">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-white">Weekly Report</span>
                    <span className="text-[10px] text-zinc-500">May 12–18</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="bg-white/5 rounded-lg p-2 text-center">
                      <div className="text-sm font-bold text-white">23</div>
                      <div className="text-[9px] text-zinc-500">Received</div>
                    </div>
                    <div className="bg-rose-500/10 rounded-lg p-2 text-center">
                      <div className="text-sm font-bold text-rose-500">20</div>
                      <div className="text-[9px] text-zinc-500">Filtered</div>
                    </div>
                    <div className="bg-emerald-500/10 rounded-lg p-2 text-center">
                      <div className="text-sm font-bold text-emerald-500">3</div>
                      <div className="text-[9px] text-zinc-500">For You</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {['RevOps Platform — 91%', 'Analytics Suite — 84%', 'CloudSync Pro — 78%'].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        <span className="text-[11px] text-zinc-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ),
            },
          ].map((step, i) => (
            <div
              key={i}
              className="grid lg:grid-cols-12 gap-8 items-center p-8 rounded-2xl border border-white/5 hover:border-rose-500/20 bg-[#18181b]/50 transition-all"
            >
              <div className="lg:col-span-1">
                <span className="text-5xl font-bold text-zinc-800">{step.step}</span>
              </div>
              <div className="lg:col-span-5">
                <h3 className="text-2xl font-semibold text-white mb-3">{step.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{step.description}</p>
              </div>
              <div className="lg:col-span-6 flex justify-center lg:justify-end">
                {step.mockup}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonial
const TestimonialSection = () => {
  return (
    <section className="py-24 lg:py-32 bg-[#0f0f10]">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <blockquote className="text-2xl sm:text-3xl lg:text-4xl text-white leading-relaxed font-light mb-8">
          "I evaluated 40 vendor pitches last quarter. Three were relevant.
          That's a <span className="text-rose-500">92% waste</span> of my time."
        </blockquote>
        <div className="text-zinc-500">
          Head of Operations, Series B Fintech
        </div>
      </div>
    </section>
  );
};

// Built For Teams Section
const BuiltForTeamsSection = () => {
  const industrySegments = [
    { icon: ShoppingCart, label: 'Ecommerce' },
    { icon: CreditCard, label: 'Payments' },
    { icon: BarChart3, label: 'Fintech' },
    { icon: Code2, label: 'SaaS' },
    { icon: Users, label: 'B2B' },
    { icon: Store, label: 'Marketplace' },
  ];

  const trustBadges = [
    { icon: ShieldCheck, label: 'SOC 2 Compliant' },
    { icon: Lock, label: 'End-to-End Encrypted' },
    { icon: Zap, label: '< 24hr Response' },
  ];

  return (
    <section className="py-20 bg-[#0a0a0b] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Industry segments */}
        <p className="text-xs font-semibold text-rose-500 tracking-widest uppercase text-center mb-6">
          Built for teams in
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          {industrySegments.map((seg, i) => {
            const Icon = seg.icon;
            return (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2.5 bg-white/5 rounded-full border border-white/10 hover:border-rose-500/30 hover:bg-rose-500/5 transition-all duration-300 cursor-default"
              >
                <Icon className="w-3.5 h-3.5 text-rose-500" />
                <span className="text-sm font-medium text-zinc-300">{seg.label}</span>
              </div>
            );
          })}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-12">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs font-semibold text-rose-500 tracking-widest uppercase">Trusted & Secure</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-6">
          {trustBadges.map((badge, i) => {
            const Icon = badge.icon;
            return (
              <div key={i} className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
                <Icon className="w-3.5 h-3.5 text-rose-500" />
                <span className="text-xs font-medium text-zinc-300">{badge.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Case Study Section
const CaseStudySection = () => {
  const vendorPitches = [
    { name: 'AdPulse Pro', category: 'Marketing', claim: '"3x your ROAS in 30 days"', verdict: 'filtered' as const },
    { name: 'PayStream 360', category: 'Payments', claim: '"Lowest fees in the industry"', verdict: 'filtered' as const },
    { name: 'MailBlitz', category: 'Email', claim: '"500% ROI guaranteed"', verdict: 'filtered' as const },
    { name: 'ConvertMax', category: 'CRO Tool', claim: '"Boost conversions by 40%"', verdict: 'recommended' as const },
    { name: 'ShipFast Pro', category: 'Fulfillment', claim: '"Same-day delivery"', verdict: 'filtered' as const },
    { name: 'CheckoutPlus', category: 'Checkout', claim: '"Recover 25% of carts"', verdict: 'recommended' as const },
  ];

  const results = [
    { icon: ShoppingBag, value: '47', label: 'Vendor pitches received' },
    { icon: TrendingUp, value: '2', label: 'Worth adopting' },
    { icon: Clock, value: '18hrs', label: 'Time saved' },
    { icon: DollarSign, value: '$34K', label: 'Saved vs. vendor pricing' },
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#0f0f10] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-500/10 border border-rose-500/20 rounded-full mb-6">
            <ShoppingBag className="w-3.5 h-3.5 text-rose-500" />
            <span className="text-xs font-medium text-rose-400 tracking-wide uppercase">
              Case Study · E-Commerce Retail
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            47 vendor pitches.
            <span className="text-zinc-600"> 2 worth pursuing.</span>
          </h2>
          <p className="text-zinc-400 text-lg leading-relaxed">
            A growing online retailer was drowning in pitches from marketing platforms, payment gateways,
            and fulfillment services. They had no way to verify any of it.
          </p>
        </div>

        {/* Content grid */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 mb-16">
          {/* Story */}
          <div className="space-y-8">
            <div>
              <h3 className="text-sm font-semibold text-rose-500 tracking-widest uppercase mb-3">The Challenge</h3>
              <p className="text-zinc-400 leading-relaxed">
                Every week, the merchant's inbox flooded with cold emails from sales reps. Every vendor claimed to be
                "the best" with impressive-sounding numbers — but there was no way to verify any claims.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-rose-500 tracking-widest uppercase mb-3">The Solution</h3>
              <p className="text-zinc-400 leading-relaxed">
                Saleswall intercepted every vendor pitch, verified their claims against market data, compared
                pricing transparently, and assessed fit. Out of 47 pitches, only 2 made the cut.
              </p>
            </div>

            {/* Before / After */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-xl p-5 border border-white/5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-zinc-500" />
                  <span className="text-xs font-semibold text-zinc-500 tracking-wide uppercase">Before</span>
                </div>
                <div className="space-y-2">
                  {['Unverified claims', 'Hidden pricing', 'Hours in demos', 'Decision fatigue'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <X className="w-3.5 h-3.5 text-zinc-600" />
                      <span className="text-sm text-zinc-500">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#18181b] rounded-xl p-5 border border-white/10">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-xs font-semibold text-zinc-400 tracking-wide uppercase">After</span>
                </div>
                <div className="space-y-2">
                  {['Claims verified', 'Transparent pricing', 'Zero wasted demos', 'Clear decisions'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500" />
                      <span className="text-sm text-zinc-300">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Vendor evaluation mockup */}
          <div className="bg-[#18181b] rounded-2xl p-5 border border-white/10 shadow-xl h-fit">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-medium text-zinc-400">Saleswall Evaluation — Monthly Summary</span>
              <span className="text-[10px] px-2 py-0.5 bg-rose-500/10 text-rose-400 rounded-full">E-Commerce</span>
            </div>
            <div className="space-y-1.5">
              {vendorPitches.map((vendor, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-white/5 border border-white/5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-white font-medium">{vendor.name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-white/5 text-zinc-500 rounded">{vendor.category}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 italic truncate">{vendor.claim}</div>
                  </div>
                  <span className={`text-[10px] px-2.5 py-1 rounded-full font-medium flex-shrink-0 ml-3 ${
                    vendor.verdict === 'recommended'
                      ? 'bg-rose-500/10 text-rose-400'
                      : 'bg-white/5 text-zinc-500'
                  }`}>
                    {vendor.verdict === 'recommended' ? 'Recommended' : 'Filtered'}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
              <span className="text-[11px] text-zinc-500">47 pitches · 2 recommended</span>
              <span className="text-[11px] text-rose-500 font-medium">95.7% noise filtered</span>
            </div>
          </div>
        </div>

        {/* Results stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {results.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div key={i} className="bg-[#18181b] rounded-xl p-6 border border-white/5 hover:border-rose-500/20 transition-all">
                <Icon className="w-5 h-5 text-rose-500 mb-3" />
                <div className="text-2xl sm:text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-xs text-zinc-500">{stat.label}</div>
              </div>
            );
          })}
        </div>

        {/* Shark Tank Quote */}
        <div className="max-w-3xl mx-auto text-center">
          <div className="relative bg-[#18181b] rounded-2xl px-8 py-10 lg:px-12 lg:py-12 border border-white/10">
            {/* Decorative quotes */}
            <svg className="absolute top-6 left-8 w-10 h-10 text-rose-500/20" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
            </svg>

            <blockquote className="relative z-10 text-xl lg:text-2xl font-medium text-white leading-relaxed mb-6">
              Saleswall is like having your personal <span className="text-rose-500">Shark Tank</span> in your pocket —
              every vendor gets evaluated, only the real deals make it through.
            </blockquote>

            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-500/10 flex items-center justify-center">
                <span className="text-xs font-semibold text-rose-500">EC</span>
              </div>
              <div className="text-left">
                <div className="text-sm font-semibold text-zinc-200">E-Commerce Founder</div>
                <div className="text-xs text-zinc-500">Online Retail · Early Access Member</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      await axios.post(getApiUrl('/api/waitlist'), { email: email.trim(), source: 'cta' });
      setStatus('success');
      setEmail('');
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <section className="py-24 lg:py-32 bg-[#0a0a0b] relative overflow-hidden">
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: 'radial-gradient(circle at 50% 50%, #f43f5e 0%, transparent 40%)',
      }} />

      <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-500/10 border border-rose-500/20 rounded-full mb-8">
          <Sparkles className="w-3.5 h-3.5 text-rose-400" />
          <span className="text-xs font-medium text-rose-400 tracking-wide uppercase">
            Early access opening soon
          </span>
        </div>

        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
          Ready for clarity?
        </h2>

        <p className="text-xl text-zinc-400 mb-10 leading-relaxed max-w-2xl mx-auto">
          Join the waitlist to get early access. Our first cohort is limited —
          we want to deliver an incredible experience from day one.
        </p>

        {status === 'success' ? (
          <div className="text-emerald-400 text-lg mb-8">
            You're on the list. We'll be in touch.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto mb-6">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@company.com"
              required
              disabled={status === 'loading'}
              className="w-full sm:flex-1 px-5 py-4 text-base border border-white/10 rounded-xl focus:outline-none focus:border-rose-500/50 bg-white/5 text-white transition-all placeholder:text-zinc-500"
            />
            <Button
              type="submit"
              disabled={status === 'loading'}
              className="w-full sm:w-auto bg-rose-500 hover:bg-rose-600 text-white px-8 py-4 h-auto text-base font-medium rounded-xl transition-all hover:shadow-xl hover:shadow-rose-500/25"
            >
              {status === 'loading' ? 'Joining...' : 'Join Waitlist'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>
        )}

        <p className="text-sm text-zinc-600">
          Limited early access · We'll reach out when it's your turn
        </p>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 px-6 bg-[#0a0a0b] border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <LogoLight />
          <div className="flex items-center gap-8 text-sm text-zinc-500">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <a href="#waitlist" className="hover:text-white transition-colors">Join Waitlist</a>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-white/5 text-center text-sm text-zinc-600">
          © {new Date().getFullYear()} Saleswall
        </div>
      </div>
    </footer>
  );
};

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Header />
      <HeroSection />
      <BuiltForTeamsSection />
      <ProblemSection />
      <CaseStudySection />
      <HowItWorksSection />
      <TestimonialSection />
      <CTASection />
      <Footer />
    </div>
  );
}
